import { useContext, useEffect, useRef } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useLocation } from 'react-router-dom';
import { UserContext } from 'contexts/ConfigContext';
import { FilterContextData } from 'contexts/FilterContext';
import { getUserScopeQuery } from 'gqlQueries/filterQueries';
import { isPpaRoutePath, PPA_HOME_ROUTE } from 'config/urls';
import {
    applyFilterMutation,
    buildScopeDimensionPayloads,
    hasPersistedUserScope,
    hasPpaScopeFilterIds,
} from 'hooks/ppa/ppaScope.helpers';
import { getConfig, getScreenFilterDetails } from 'utils/helperMethods';

/**
 * Restores persisted PPA scope (Market × Category) on non-home PPA routes.
 * ScopeRibbon handles hydration on the homepage; this covers direct loads
 * and reloads of pages such as Performance Summary.
 */
export function usePpaScopeHydration() {
    const route = useLocation();
    const { pathname } = route;
    const { appConfig } = useContext(UserContext);
    const globalFilterConfig = getConfig('globalFilter', appConfig);
    const { currentScreenFeatureName } = getScreenFilterDetails(route, globalFilterConfig);
    const {
        ppaScopeFilterIds,
        setPpaScopeFilterIds,
        setPpaScopeFilterLoading,
        setPpaPersistedScope,
    } = useContext(FilterContextData);
    const hasRequestedRef = useRef(false);
    const userEmail = localStorage.getItem('email') || '';

    const [resolveScopeSelections] = useMutation(applyFilterMutation, {
        onCompleted: (response) => {
            setPpaScopeFilterIds(response);
            setPpaScopeFilterLoading(false);
        },
        onError: () => {
            setPpaScopeFilterLoading(false);
        },
    });

    const [fetchUserScope] = useLazyQuery(getUserScopeQuery, {
        fetchPolicy: 'network-only',
        onCompleted: async (response) => {
            const persistedScope = response?.getUserScope?.userScope ?? response?.getUserScope;
            if (!hasPersistedUserScope(persistedScope)) {
                setPpaScopeFilterLoading(false);
                return;
            }

            setPpaPersistedScope(persistedScope);

            if (hasPpaScopeFilterIds(ppaScopeFilterIds)) {
                setPpaScopeFilterLoading(false);
                return;
            }

            try {
                await resolveScopeSelections({
                    variables: {
                        ...buildScopeDimensionPayloads(persistedScope),
                        featureName: currentScreenFeatureName || 'default',
                    },
                });
            } catch {
                setPpaScopeFilterLoading(false);
            }
        },
        onError: () => {
            setPpaScopeFilterLoading(false);
        },
    });

    useEffect(() => {
        hasRequestedRef.current = false;
    }, [pathname]);

    useEffect(() => {
        if (!isPpaRoutePath(pathname) || pathname.includes(PPA_HOME_ROUTE)) return;
        if (hasPpaScopeFilterIds(ppaScopeFilterIds)) {
            setPpaScopeFilterLoading(false);
            return;
        }
        if (hasRequestedRef.current || !userEmail) {
            if (!userEmail) setPpaScopeFilterLoading(false);
            return;
        }

        hasRequestedRef.current = true;
        setPpaScopeFilterLoading(true);
        fetchUserScope({ variables: { userId: userEmail } });
    }, [pathname, userEmail, ppaScopeFilterIds]);
}
